import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './SoundRecordist.module.css';
import ReactPlayer from 'react-player';
import { IoCloseOutline } from "react-icons/io5";
import { trackVideoInteraction, trackExternalLink, trackNavigation, trackButtonClick, trackModal } from '../utils/analytics';

const videos = [
  {
    id: 8,
    title: 'Lumberjack',
    pageLink: '/lumberjack',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8ce386c5-29bd-4ab5-86c7-f372368dbce2-lumberjack%20poster.jpg',
  },
  {
    id: 1,
    title: 'Hello Goodbye',
    videoUrl: 'https://youtu.be/mwMSRdp6Fwc', 
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/9dd569eb-3340-41c2-b092-b40c0764f532-Hello_Goodbye-smaller.jpg',
  },
  {
    id: 2,
    title: 'Buzzfeed',
    website: 'https://www.youtube.com/watch?v=5YnLBGf2bQs', 
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/2916a819-6f31-45eb-9519-14a050b596eb-Buzzfeed%20Presents%20poster.jpg',
  },
  {
    id: 7,
    title: 'Adidas Paris',
    videoUrl: 'https://video.wixstatic.com/video/7d2b5d_48c473509b234301a0af42a8faeeac5b/1080p/mp4/file.mp4',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/42c315df-2fef-4333-9459-7f2d36287bb0-adidas%20paris.png',
    description: 'Record vintage style drums and mix to tape'
  },
  {
    id: 3,
    title: 'John Sunshine JSLRNRT',
    videoUrl: 'https://vimeo.com/686909888',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/b0691f10-c810-458f-8e87-4ae5042dd9ef-John%20Sunshine%20JSLRNRT%20poster.png',
  },
  {
    id: 6,
    title: 'The Portland Sessions',
    website: 'https://www.theportlandsessions.com/',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/dc29cbde-ed3b-4ed6-adcf-e7ae29d5f505-The%20Portland%20Sessions%20logo.png',
  },
  {
    id: 2,
    title: 'Agnes Addington',
    website: 'https://bencleek.wixsite.com/benjamincleek/agnes-addington', 
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/7206a2d5-8a91-4eca-af59-83d91a757710-Agnes%20Addington.jpg',
  },
  {
    id: 5,
    title: 'Slowly Quickly',
    videoUrl: 'https://vimeo.com/11598388',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/0a323ad9-b610-4377-a693-d1ce5c707c65-Slowly%20Quickly%20%281%29.jpg',
  },
];

const VideoCard = ({ video, onClick }) => (
  <div
    className={styles.videoCard}
    onClick={(event) => onClick(video, event)}
  >
    <div className={styles.imageWrapper}>
      <img className={styles.videoImg} src={video.thumbnail} alt={video.title} />
    </div>
    <div className={styles.cardInfo}>
      <p className={styles.cardTitle}>{video.title}</p>
    </div>
  </div>
);

const SoundRecordist = () => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlayerVisible, setPlayerVisible] = useState(false);
  const playerRef = useRef(null);
  const enteredFsRef = useRef(false);

  const requestMobileFullscreen = () => {
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;
    const el = document.documentElement;
    const fsReq = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (fsReq) {
      enteredFsRef.current = true;
      fsReq.call(el).then(() => screen.orientation?.unlock?.()).catch(() => {
        enteredFsRef.current = false;
      });
    }
  };

  const exitMobileFullscreen = () => {
    if (!enteredFsRef.current) return;
    enteredFsRef.current = false;
    (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
  };

  const handleVideoClick = (video, event) => {
    if (video.website) {
      trackExternalLink(video.website, video.title);
      window.open(video.website, '_blank');
      return;
    }

    if (video.pageLink) {
      trackNavigation(video.title, video.pageLink);
      navigate(video.pageLink);
      return;
    }

    if (selectedVideo && selectedVideo.id === video.id) {
      trackVideoInteraction('close', video.title, 'SoundRecordist');
      exitMobileFullscreen();
      setSelectedVideo(null);
      setPlayerVisible(false);
    } else {
      trackVideoInteraction('play', video.title, 'SoundRecordist');
      trackModal('open', `SoundRecordist - ${video.title}`);
      setSelectedVideo(video);
      setPlayerVisible(true);
      requestMobileFullscreen();
    }

    event.stopPropagation();
  };

  const handleCloseModal = () => {
    if (selectedVideo) {
      trackVideoInteraction('close', selectedVideo.title, 'SoundRecordist');
      trackModal('close', `SoundRecordist - ${selectedVideo.title}`);
    }
    exitMobileFullscreen();
    setPlayerVisible(false);
    setSelectedVideo(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        playerRef.current &&
        !playerRef.current.contains(event.target) &&
        event.target.className !== styles.button &&
        event.target.closest(`.${styles.modalContent}`) === null
      ) {
        handleCloseModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [playerRef]);

  useEffect(() => {
    const onFsChange = () => {
      if (!(document.fullscreenElement ?? document.webkitFullscreenElement) && enteredFsRef.current) {
        enteredFsRef.current = false;
        setPlayerVisible(false);
        setSelectedVideo(null);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  return (
    <div className={styles.soundRecordist}>
      <div className={styles.videoThumbs}>
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} onClick={handleVideoClick} />
        ))}
        {selectedVideo && isPlayerVisible && (
          <div className={styles.modal}>
            <div className={styles.modalContent} ref={playerRef}>
              {selectedVideo.videoUrl && (
                <ReactPlayer
                  url={selectedVideo.videoUrl}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  controls
                  playing
                />
              )}
              <button 
                onClick={handleCloseModal} 
                className={styles.button}
                onMouseDown={(e) => {
                  trackButtonClick('close_modal', 'SoundRecordist', { video_title: selectedVideo?.title });
                }}
              >
                <IoCloseOutline />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundRecordist;
